/**
 * Reading preferences.
 *
 * Text size and line spacing, because the right values for a two-hour session
 * are personal and depend on the screen you are at. They are written to
 * `<html>` as custom properties, so the prose stylesheet consumes them and
 * nothing else in the layout has to know.
 *
 * Deliberately only these two. A "settings" panel with a dozen knobs is a
 * different kind of product; these are the two that change whether a long
 * derivation is comfortable.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const STORAGE_KEY = "szero.reading";

export const TEXT_SIZES = [
  { id: "small", label: "Small", scale: 0.92 },
  { id: "default", label: "Default", scale: 1 },
  { id: "large", label: "Large", scale: 1.12 },
  { id: "xlarge", label: "Larger", scale: 1.26 },
] as const;

export type TextSize = (typeof TEXT_SIZES)[number]["id"];

export const LINE_SPACINGS = [
  { id: "tight", label: "Tight", leading: 1.6 },
  { id: "normal", label: "Normal", leading: 1.78 },
  { id: "relaxed", label: "Relaxed", leading: 2 },
] as const;

export type LineSpacing = (typeof LINE_SPACINGS)[number]["id"];

export interface ReadingPreferences {
  readonly size: TextSize;
  readonly spacing: LineSpacing;
}

const DEFAULTS: ReadingPreferences = { size: "default", spacing: "normal" };

interface ReadingContextValue extends ReadingPreferences {
  readonly setSize: (size: TextSize) => void;
  readonly setSpacing: (spacing: LineSpacing) => void;
  readonly reset: () => void;
  readonly isDefault: boolean;
}

const ReadingContext = createContext<ReadingContextValue>({
  ...DEFAULTS,
  setSize: () => {},
  setSpacing: () => {},
  reset: () => {},
  isDefault: true,
});

function readStored(): ReadingPreferences {
  if (typeof window === "undefined") return DEFAULTS;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;

    const parsed = JSON.parse(raw) as Partial<ReadingPreferences>;
    return {
      size: TEXT_SIZES.some((s) => s.id === parsed.size)
        ? (parsed.size as TextSize)
        : DEFAULTS.size,
      spacing: LINE_SPACINGS.some((s) => s.id === parsed.spacing)
        ? (parsed.spacing as LineSpacing)
        : DEFAULTS.spacing,
    };
  } catch {
    // A corrupt or unreadable preference is not worth failing a page load over.
    return DEFAULTS;
  }
}

export function ReadingProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<ReadingPreferences>(readStored);

  useEffect(() => {
    const root = document.documentElement;
    const scale = TEXT_SIZES.find((s) => s.id === preferences.size)!.scale;
    const leading = LINE_SPACINGS.find((s) => s.id === preferences.spacing)!.leading;

    root.style.setProperty("--reading-scale", String(scale));
    root.style.setProperty("--reading-leading", String(leading));

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // Private browsing; the session still works, it just won't be remembered.
    }
  }, [preferences]);

  const value = useMemo<ReadingContextValue>(
    () => ({
      ...preferences,
      setSize: (size) => setPreferences((current) => ({ ...current, size })),
      setSpacing: (spacing) =>
        setPreferences((current) => ({ ...current, spacing })),
      reset: () => setPreferences(DEFAULTS),
      isDefault:
        preferences.size === DEFAULTS.size &&
        preferences.spacing === DEFAULTS.spacing,
    }),
    [preferences],
  );

  return (
    <ReadingContext.Provider value={value}>{children}</ReadingContext.Provider>
  );
}

export const useReading = (): ReadingContextValue => useContext(ReadingContext);
